// src/modules/dl/LLMLab.jsx
// LLM Lab — How large language models work under the hood
// Tokenization · Sampling · Temperature · KV Cache · Context Window · Scaling Laws

import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = 'var(--gold)';
const P  = ['#fbbf24','#22d3ee','#4ade80','#a78bfa','#f87171','#fb923c','#60a5fa'];

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,letterSpacing:'.08em',opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=0.01,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?4:step<1?2:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);

// ─── FAKE VOCAB / TOKENIZER ──────────────────────────────────────────────────
const VOCAB = [
  'the','cat','sat','on','mat','dog','ran','fast','in','a','The','quick','brown','fox',
  'jumps','over','lazy','hello','world','deep','learning','is','amazing','model','token',
  'neural','network','attention','transformer','language','<eos>','<pad>','<unk>',
];

// BPE-style fake tokenizer — splits on spaces and maps to token IDs
const tokenize = (text) => {
  const words = text.trim().split(/\s+/).filter(Boolean);
  return words.map(w => {
    const lower = w.toLowerCase().replace(/[^a-z]/g,'');
    const idx = VOCAB.findIndex(v=>v.toLowerCase()===lower);
    return { text: w, id: idx>=0?idx:VOCAB.length-1, isKnown: idx>=0 };
  });
};

// Fake next-token probability distribution (higher for "related" words)
const nextTokenProbs = (context, vocab, temperature=1.0) => {
  const seed = context.reduce((s,t)=>s+t.id*7,0);
  const raw = vocab.map((_,i)=>{
    const base = Math.abs(Math.sin(seed*0.1+i*0.7))*2 + Math.abs(Math.cos(i*0.3+seed*0.05));
    return Math.max(0.001, base);
  });
  // Apply temperature
  const logits = raw.map(v=>Math.log(v)/temperature);
  const max = Math.max(...logits);
  const exps = logits.map(v=>Math.exp(v-max));
  const sum = exps.reduce((a,b)=>a+b,0);
  return exps.map(v=>v/sum);
};

// Top-K sampling
const topKSample = (probs, k) => {
  const sorted = probs.map((p,i)=>({p,i})).sort((a,b)=>b.p-a.p);
  const topk = sorted.slice(0,k);
  const sum = topk.reduce((s,x)=>s+x.p,0);
  const norm = topk.map(x=>({...x, p:x.p/sum}));
  return norm;
};

// Top-P (nucleus) sampling
const topPSample = (probs, p) => {
  const sorted = probs.map((p2,i)=>({p:p2,i})).sort((a,b)=>b.p-a.p);
  let cumSum=0, cut=sorted.length;
  for(let i=0;i<sorted.length;i++){cumSum+=sorted[i].p;if(cumSum>=p){cut=i+1;break;}}
  const nucleus = sorted.slice(0,cut);
  const sum = nucleus.reduce((s,x)=>s+x.p,0);
  return nucleus.map(x=>({...x,p:x.p/sum}));
};

const TABS = ['tokenizer','sampling','temperature','kv-cache','context-window','scaling-laws'];

// ─── TOKEN CHIP ──────────────────────────────────────────────────────────────
function TokenChip({token, color, showId=true}) {
  const colors = ['#22d3ee','#4ade80','#a78bfa','#fbbf24','#fb923c','#f87171','#60a5fa','#34d399','#f472b6'];
  const c = color || colors[token.id % colors.length];
  return (
    <span style={{
      display:'inline-flex', flexDirection:'column', alignItems:'center',
      background: c+'18', border:`1px solid ${c}44`, borderRadius:'4px',
      padding:'4px 8px', margin:'2px', cursor:'default',
    }}>
      <span style={{fontSize:'13px', color:c, fontFamily:'JetBrains Mono', fontWeight:700}}>{token.text}</span>
      {showId&&<span style={{fontSize:'8px', color:'var(--t3)', fontFamily:'JetBrains Mono'}}>#{token.id}</span>}
    </span>
  );
}

// ─── PROBABILITY BAR ─────────────────────────────────────────────────────────
function ProbBar({word, prob, color, rank, maxProb=1}) {
  const pct = (prob/maxProb)*100;
  return (
    <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'4px'}}>
      <span style={{fontSize:'9px', color:'var(--t3)', minWidth:16, textAlign:'right'}}>{rank}</span>
      <span style={{fontSize:'10px', color, fontFamily:'JetBrains Mono', minWidth:80, fontWeight:700}}>{word}</span>
      <div style={{flex:1, height:14, background:'var(--b0)', borderRadius:2, overflow:'hidden'}}>
        <div style={{height:'100%', width:`${pct}%`, background:`linear-gradient(90deg,${color}44,${color})`, borderRadius:2, transition:'width .3s'}}/>
      </div>
      <span style={{fontSize:'9px', color, fontFamily:'JetBrains Mono', minWidth:50, textAlign:'right'}}>{(prob*100).toFixed(2)}%</span>
    </div>
  );
}

// ─── KV CACHE SVG ────────────────────────────────────────────────────────────
function KVCacheSVG({seqLen, cacheUsed, numLayers=6, numHeads=4}) {
  const W=700, H=200, PAD=20;
  const tokenW = (W-PAD*2)/seqLen;
  const layerH = (H-PAD*2)/numLayers;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{background:'#060b14', borderRadius:'8px', border:'1px solid #1e293b', display:'block'}}>
      {/* Layer rows */}
      {Array.from({length:numLayers},(_,l)=>(
        <g key={l}>
          <text x={PAD-4} y={PAD+l*layerH+layerH/2} textAnchor="end" dominantBaseline="middle"
            style={{fontSize:'8px', fill:'#64748b', fontFamily:'JetBrains Mono'}}>L{l+1}</text>
          {/* KV slots */}
          {Array.from({length:seqLen},(_,t)=>{
            const cached = cacheUsed && t < cacheUsed;
            const isNew = cacheUsed && t === cacheUsed;
            const x = PAD + t*tokenW, y = PAD + l*layerH;
            const fill = cached ? '#22d3ee' : isNew ? '#4ade80' : '#1e293b';
            const stroke = cached ? '#22d3ee66' : isNew ? '#4ade80' : '#334155';
            return (
              <g key={t}>
                <rect x={x+1} y={y+2} width={tokenW-4} height={layerH-6}
                  fill={fill+'22'} stroke={stroke} strokeWidth={cached||isNew?1.5:1} rx={2}/>
                {cached && <text x={x+tokenW/2} y={y+layerH/2} textAnchor="middle" dominantBaseline="middle"
                  style={{fontSize:'7px', fill:'#22d3ee', fontFamily:'JetBrains Mono'}}>KV</text>}
                {isNew && <text x={x+tokenW/2} y={y+layerH/2} textAnchor="middle" dominantBaseline="middle"
                  style={{fontSize:'7px', fill:'#4ade80', fontFamily:'JetBrains Mono', fontWeight:700}}>NEW</text>}
              </g>
            );
          })}
        </g>
      ))}
      {/* Legend */}
      {[['#22d3ee','Cached KV (reused)'],['#4ade80','New token (compute only this)'],['#1e293b','Empty']].map(([c,l],i)=>(
        <g key={i}>
          <rect x={PAD+i*200} y={H-14} width={10} height={8} fill={c+'44'} stroke={c} rx={1}/>
          <text x={PAD+i*200+14} y={H-10} style={{fontSize:'8px', fill:'#64748b', fontFamily:'JetBrains Mono'}}>{l}</text>
        </g>
      ))}
    </svg>
  );
}

export default function LLMLab() {
  const [tab, setTab] = useState('tokenizer');
  const [logs, setLogs] = useState([{ts:'00:00:00',msg:'LLM Lab — explore how LLMs tokenize, sample, and generate text.',t:'info'}]);
  const log = (msg,t='info') => setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  // TOKENIZER
  const [inputText, setInputText] = useState('The quick brown fox jumps over the lazy dog');
  const [tokens, setTokens] = useState([]);

  // SAMPLING
  const [samplingMethod, setSamplingMethod] = useState('topk');
  const [topK, setTopK] = useState(5);
  const [topP, setTopP] = useState(0.9);
  const [sampTemp, setSampTemp] = useState(1.0);
  const [sampResult, setSampResult] = useState(null);
  const [contextText, setContextText] = useState('The cat sat');
  const [generated, setGenerated] = useState([]);

  // TEMPERATURE
  const [tempVal, setTempVal] = useState(1.0);
  const [tempResult, setTempResult] = useState(null);

  // KV CACHE
  const [kvSeqLen, setKvSeqLen] = useState(16);
  const [kvCachePos, setKvCachePos] = useState(10);

  // CONTEXT WINDOW
  const [ctxSize, setCtxSize] = useState(4096);

  // SCALING
  const [scalingResult, setScalingResult] = useState(null);

  // Auto-tokenize on input change
  useEffect(() => {
    const toks = tokenize(inputText);
    setTokens(toks);
  }, [inputText]);

  const runSampling = () => {
    const ctx = tokenize(contextText);
    const probs = nextTokenProbs(ctx, VOCAB, sampTemp);
    let filtered;
    if(samplingMethod==='topk') filtered = topKSample(probs, topK);
    else if(samplingMethod==='topp') filtered = topPSample(probs, topP);
    else filtered = probs.map((p,i)=>({p,i})).sort((a,b)=>b.p-a.p).slice(0,10);
    setSampResult({filtered, probs, ctx});
    log(`${samplingMethod.toUpperCase()} sampling: top ${filtered.length} tokens from vocab=${VOCAB.length}, temp=${sampTemp}`,'ok');
  };

  const generateNext = () => {
    const ctx = [...tokenize(contextText), ...generated];
    const probs = nextTokenProbs(ctx, VOCAB, sampTemp);
    let filtered;
    if(samplingMethod==='topk') filtered = topKSample(probs, topK);
    else filtered = topPSample(probs, topP);
    // Sample from distribution
    const r = Math.random();
    let cumSum = 0;
    let chosen = filtered[filtered.length-1];
    for(const item of filtered){ cumSum += item.p; if(r<=cumSum){chosen=item;break;} }
    const tok = {text: VOCAB[chosen.i]||'<unk>', id: chosen.i, isKnown: true};
    setGenerated(g=>[...g, tok]);
    log(`Generated: "${tok.text}" (id=${tok.id}, p=${(chosen.p*100).toFixed(2)}%)`,'ok');
  };

  const runTemperature = () => {
    const ctx = tokenize('The cat');
    const temps = [0.1, 0.5, 1.0, 1.5, 2.0, 3.0];
    const results = temps.map(t => {
      const probs = nextTokenProbs(ctx, VOCAB, t);
      const sorted = probs.map((p,i)=>({p,i})).sort((a,b)=>b.p-a.p);
      const entropy = -probs.reduce((s,p)=>s+(p>0?p*Math.log(p+1e-10):0),0);
      return { temp:t, top1:VOCAB[sorted[0].i], top1p:+(sorted[0].p*100).toFixed(1), entropy:+entropy.toFixed(3), spread: +(sorted[0].p - sorted[9]?.p).toFixed(4) };
    });
    setTempResult(results);
    log(`Temperature analysis: 0.1→3.0 | high T = uniform distribution`,'ok');
  };

  const runScaling = () => {
    // Chinchilla scaling laws: L(N,D) ≈ E + A/N^α + B/D^β
    const models = [
      {name:'125M', N:125e6, flops:1e18},
      {name:'350M', N:350e6, flops:3e18},
      {name:'1.3B', N:1.3e9, flops:1e19},
      {name:'6.7B', N:6.7e9, flops:6e19},
      {name:'13B',  N:13e9,  flops:1e20},
      {name:'30B',  N:30e9,  flops:3e20},
      {name:'70B',  N:70e9,  flops:7e20},
      {name:'175B', N:175e9, flops:2e21},
    ];
    const E=1.69, A=406.4, B=410.7, alpha=0.336, beta=0.283;
    const results = models.map(m=>{
      const D = m.flops / (6*m.N);
      const loss = E + A/Math.pow(m.N,alpha) + B/Math.pow(D,beta);
      return {name:m.name, N:m.N, tokens:+(D/1e9).toFixed(1)+'B', loss:+loss.toFixed(3), params:+(m.N/1e9).toFixed(2)+'B'};
    });
    setScalingResult(results);
    log(`Scaling laws: Chinchilla-optimal compute allocation for 8 model sizes`,'ok');
  };

  // Temperature distribution for visualization
  const tempDistData = (() => {
    const ctx = tokenize('The');
    return [0.3, 1.0, 2.5].map(t => {
      const probs = nextTokenProbs(ctx, VOCAB, t);
      return probs.map((p,i)=>({token: VOCAB[i], prob:p, temp:t})).sort((a,b)=>b.prob-a.prob).slice(0,8);
    });
  })();

  return (
    <div className="anim-fadeup" style={{display:'flex', flexDirection:'column', gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* ── TOKENIZER ── */}
      {tab==='tokenizer'&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr',gap:'12px'}}>
          <Panel title="BPE TOKENIZER VISUALIZER" accent={AC}>
            <div style={{marginBottom:'10px'}}>
              <div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'5px'}}>INPUT TEXT</div>
              <input value={inputText} onChange={e=>setInputText(e.target.value)}
                style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'10px 12px',fontSize:'13px',fontFamily:'JetBrains Mono',borderRadius:'4px',outline:'none',boxSizing:'border-box'}}
                placeholder="Type any text to see tokens..."/>
            </div>
            <div style={{display:'flex',flexWrap:'wrap',padding:'12px',background:'#060b14',borderRadius:'6px',border:'1px solid #1e293b',minHeight:60}}>
              {tokens.map((tok,i)=><TokenChip key={i} token={tok} color={tok.isKnown?undefined:'#f87171'}/>)}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px',marginTop:'10px'}}>
              <Metric label="Tokens" value={tokens.length} color={AC}/>
              <Metric label="Characters" value={inputText.length} color="#22d3ee"/>
              <Metric label="Chars/Token" value={tokens.length>0?(inputText.length/tokens.length).toFixed(2):0} color="#4ade80"/>
              <Metric label="Unknown tokens" value={tokens.filter(t=>!t.isKnown).length} color="#f87171"/>
            </div>
          </Panel>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
            <Panel title="TOKEN ID TABLE" accent="#22d3ee">
              <div style={{maxHeight:200,overflowY:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'10px',fontFamily:'JetBrains Mono'}}>
                  <thead><tr><th style={{padding:'5px 8px',color:'var(--t3)',textAlign:'left',borderBottom:'1px solid var(--b0)'}}>Token</th><th style={{padding:'5px 8px',color:'var(--t3)',textAlign:'left',borderBottom:'1px solid var(--b0)'}}>ID</th><th style={{padding:'5px 8px',color:'var(--t3)',textAlign:'left',borderBottom:'1px solid var(--b0)'}}>Known?</th></tr></thead>
                  <tbody>
                    {tokens.map((tok,i)=>(
                      <tr key={i} style={{background:i%2===0?'var(--deep)':'transparent'}}>
                        <td style={{padding:'4px 8px',color:'#fbbf24',fontWeight:700}}>{tok.text}</td>
                        <td style={{padding:'4px 8px',color:'#22d3ee'}}>{tok.id}</td>
                        <td style={{padding:'4px 8px',color:tok.isKnown?'#4ade80':'#f87171'}}>{tok.isKnown?'✓':' ✗ OOV'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>

            <Panel title="TOKENIZATION CONCEPTS" accent="#a78bfa">
              {[
                {name:'BPE',full:'Byte Pair Encoding',desc:'Merges frequent byte pairs. GPT-2/3/4 uses this. ~50K vocab.',color:'#fbbf24'},
                {name:'WordPiece',full:'WordPiece',desc:'Splits into subwords. Used by BERT. ## prefix = continuation.',color:'#22d3ee'},
                {name:'SentencePiece',full:'SentencePiece',desc:'Language-agnostic. T5, LLaMA. ▁ prefix = word start.',color:'#4ade80'},
                {name:'Character',full:'Char-level',desc:'Each character is a token. Simple but long sequences.',color:'#a78bfa'},
              ].map(t=>(
                <div key={t.name} style={{display:'flex',gap:'10px',padding:'7px',background:'var(--deep)',borderRadius:'3px',marginBottom:'5px',borderLeft:`2px solid ${t.color}`}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:'11px',color:t.color,fontWeight:700}}>{t.name}</div>
                    <div style={{fontSize:'9px',color:'var(--t2)',marginTop:'2px'}}>{t.desc}</div>
                  </div>
                </div>
              ))}
            </Panel>
          </div>
        </div>
      )}

      {/* ── SAMPLING ── */}
      {tab==='sampling'&&(
        <div style={{display:'grid',gridTemplateColumns:'260px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="SAMPLING CONFIG" accent={AC}>
              <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'4px'}}>CONTEXT</div>
              <input value={contextText} onChange={e=>setContextText(e.target.value)}
                style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'7px 10px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',marginBottom:'10px',boxSizing:'border-box'}}/>
              <div style={{display:'flex',gap:'6px',marginBottom:'10px'}}>
                {[['greedy','GREEDY'],['topk','TOP-K'],['topp','TOP-P']].map(([v,l])=>(
                  <button key={v} onClick={()=>setSamplingMethod(v)} style={{flex:1,padding:'6px 4px',border:`1px solid ${samplingMethod===v?AC:'var(--b0)'}`,background:samplingMethod===v?AC+'18':'transparent',color:samplingMethod===v?AC:'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:700}}>{l}</button>
                ))}
              </div>
              {samplingMethod==='topk'&&<Sl label="K" value={topK} min={1} max={20} step={1} onChange={setTopK} color={AC}/>}
              {samplingMethod==='topp'&&<Sl label="P (nucleus)" value={topP} min={0.1} max={1.0} step={0.05} onChange={setTopP} color={AC}/>}
              <Sl label="Temperature" value={sampTemp} min={0.1} max={3.0} step={0.1} onChange={setSampTemp} color="#22d3ee"/>
              <Btn onClick={runSampling} color={AC} style={{width:'100%',marginBottom:'6px'}}>SAMPLE NEXT TOKEN →</Btn>
              <Btn onClick={generateNext} color="#4ade80" style={{width:'100%'}}>GENERATE + APPEND →</Btn>
            </Panel>

            {/* Generated text */}
            {generated.length>0&&(
              <Panel title="GENERATED SEQUENCE" accent="#4ade80">
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px'}}>Context + generated tokens:</div>
                <div style={{display:'flex',flexWrap:'wrap',padding:'8px',background:'#060b14',borderRadius:'4px',minHeight:40}}>
                  {tokenize(contextText).map((tok,i)=><TokenChip key={`ctx-${i}`} token={tok} color="#64748b" showId={false}/>)}
                  {generated.map((tok,i)=><TokenChip key={`gen-${i}`} token={tok} color="#4ade80" showId={false}/>)}
                </div>
                <Btn onClick={()=>setGenerated([])} color="#f87171" style={{width:'100%',marginTop:'6px',fontSize:'9px',padding:'5px'}}>CLEAR</Btn>
              </Panel>
            )}

            <LogPanel logs={logs} height={100}/>
          </div>

          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {sampResult
              ? <>
                  <Panel title={`${samplingMethod.toUpperCase()} DISTRIBUTION — TOP ${sampResult.filtered.length} TOKENS`} accent={AC}>
                    <div style={{marginBottom:'6px',fontSize:'9px',color:'var(--t3)'}}>Context: "{contextText}"  |  Sampling: {samplingMethod}  |  Temp: {sampTemp}</div>
                    {sampResult.filtered.map((item,i)=>(
                      <ProbBar key={i} word={VOCAB[item.i]||'<unk>'} prob={item.p} color={P[i%P.length]} rank={i+1} maxProb={sampResult.filtered[0].p}/>
                    ))}
                  </Panel>
                  <Panel title="FULL VOCAB PROBABILITY MASS — BEFORE AND AFTER SAMPLING" accent="#a78bfa">
                    <ResponsiveContainer width="100%" height={150}>
                      <BarChart data={sampResult.probs.map((p,i)=>({token:VOCAB[i],full:+p.toFixed(4),sampled:sampResult.filtered.find(x=>x.i===i)?+(sampResult.filtered.find(x=>x.i===i).p).toFixed(4):0})).sort((a,b)=>b.full-a.full).slice(0,16)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="token" tick={{fontSize:8,fill:'var(--t3)'}}/>
                        <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <Tooltip/>
                        <Legend iconSize={8} wrapperStyle={{fontSize:9}}/>
                        <Bar dataKey="full" fill="#a78bfa" opacity={0.5} name="Full vocab" radius={[2,2,0,0]}/>
                        <Bar dataKey="sampled" fill="#fbbf24" opacity={0.9} name={`${samplingMethod} filtered`} radius={[2,2,0,0]}/>
                      </BarChart>
                    </ResponsiveContainer>
                  </Panel>
                </>
              : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Configure sampling and click SAMPLE NEXT TOKEN</div>
            }
          </div>
        </div>
      )}

      {/* ── TEMPERATURE ── */}
      {tab==='temperature'&&(
        <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="TEMPERATURE" accent={AC}>
              <Sl label="Temperature" value={tempVal} min={0.1} max={3.0} step={0.1} onChange={setTempVal} color={AC}/>
              <div style={{padding:'8px',background:'var(--deep)',borderRadius:'3px',fontSize:'9px',color:'var(--t3)',lineHeight:1.7,marginBottom:'8px'}}>
                <div style={{color:'#4ade80',fontWeight:700,marginBottom:'3px'}}>T {'<'} 1 — Sharper</div>
                <div>More deterministic. Picks high-prob tokens.</div>
                <div style={{color:'#f87171',fontWeight:700,margin:'5px 0 3px'}}>T {'>'} 1 — Flatter</div>
                <div>More random. Low-prob tokens get a chance.</div>
              </div>
              <Btn onClick={runTemperature} color={AC} style={{width:'100%'}}>ANALYZE TEMPS →</Btn>
            </Panel>

            {/* Live prob vis at selected temp */}
            <Panel title={`LIVE: T=${tempVal}`} accent="#22d3ee">
              {(() => {
                const ctx=tokenize('The');
                const probs=nextTokenProbs(ctx,VOCAB,tempVal);
                const top=probs.map((p,i)=>({p,i})).sort((a,b)=>b.p-a.p).slice(0,6);
                return top.map((item,i)=><ProbBar key={i} word={VOCAB[item.i]} prob={item.p} color={P[i%P.length]} rank={i+1} maxProb={top[0].p}/>);
              })()}
            </Panel>
          </div>

          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {tempResult
              ? <>
                  <Panel title="TEMPERATURE vs DISTRIBUTION SHARPNESS" accent={AC}>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={tempResult}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="temp" tick={{fontSize:10,fill:'var(--t3)'}} label={{value:'temperature',fill:'var(--t3)',fontSize:9,position:'insideBottom',offset:-2}}/>
                        <YAxis yAxisId="left" tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <YAxis yAxisId="right" orientation="right" tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <Tooltip/>
                        <Legend/>
                        <Line yAxisId="left" type="monotone" dataKey="entropy" stroke="#fbbf24" strokeWidth={2} dot={{r:4}} name="Entropy (higher = more random)"/>
                        <Line yAxisId="right" type="monotone" dataKey="top1p" stroke="#22d3ee" strokeWidth={2} dot={{r:4}} name="Top-1 prob %"/>
                      </LineChart>
                    </ResponsiveContainer>
                  </Panel>

                  {/* Comparison at 3 temps */}
                  <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px'}}>
                    {[0,1,2].map(ti=>(
                      <Panel key={ti} title={`T=${[0.3,1.0,2.5][ti]}`} accent={P[ti]}>
                        {tempDistData[ti].map((item,i)=>(
                          <div key={i} style={{display:'flex',justifyContent:'space-between',marginBottom:'3px',fontSize:'9px'}}>
                            <span style={{color:P[ti],fontFamily:'JetBrains Mono',fontWeight:700}}>{item.token}</span>
                            <span style={{color:'var(--t3)'}}>{(item.prob*100).toFixed(1)}%</span>
                          </div>
                        ))}
                      </Panel>
                    ))}
                  </div>
                </>
              : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Analyze temperatures to compare distributions</div>
            }
          </div>
        </div>
      )}

      {/* ── KV CACHE ── */}
      {tab==='kv-cache'&&(
        <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="KV CACHE CONFIG" accent="#22d3ee">
              <Sl label="Sequence Length" value={kvSeqLen} min={4} max={32} step={1} onChange={setKvSeqLen} color="#22d3ee"/>
              <Sl label="Cached Up To" value={kvCachePos} min={1} max={kvSeqLen-1} step={1} onChange={setKvCachePos} color="#4ade80"/>
              <div style={{marginTop:'10px',padding:'10px',background:'var(--deep)',borderRadius:'3px',fontSize:'9px',color:'var(--t3)',lineHeight:1.8}}>
                <div style={{color:'#22d3ee',fontWeight:700,marginBottom:'4px'}}>Without KV Cache:</div>
                <div>Every token: recompute all previous K,V. O(n²) compute per step.</div>
                <div style={{color:'#4ade80',fontWeight:700,margin:'6px 0 4px'}}>With KV Cache:</div>
                <div>Store K,V from past. Only compute for the new token. O(n) per step.</div>
              </div>
            </Panel>
            <Panel title="MEMORY COST" accent="#fbbf24">
              {[['Without cache',`${(kvSeqLen*kvSeqLen*6*4*2/1024).toFixed(0)} KB`,'#f87171'],['With cache',`${(kvSeqLen*6*4*2*64/1024).toFixed(0)} KB`,'#4ade80'],['FLOPs saved',`${Math.round((1-1/kvCachePos)*100)}%`,'#fbbf24']].map(([k,v,c])=>(
                <div key={k} style={{display:'flex',justifyContent:'space-between',marginBottom:'5px',fontSize:'10px',padding:'5px',background:'var(--deep)',borderRadius:'2px'}}>
                  <span style={{color:'var(--t3)'}}>{k}</span><span style={{color:c,fontFamily:'JetBrains Mono',fontWeight:700}}>{v}</span>
                </div>
              ))}
            </Panel>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="KV CACHE VISUALIZATION — EACH ROW = LAYER, EACH COL = TOKEN POSITION" accent="#22d3ee">
              <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px'}}>Cyan = cached (reuse for free). Green = new token being computed now.</div>
              <KVCacheSVG seqLen={kvSeqLen} cacheUsed={kvCachePos} numLayers={6} numHeads={4}/>
            </Panel>
            <Panel title="COMPUTE SAVINGS PER DECODING STEP" accent="#4ade80">
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={Array.from({length:kvSeqLen},(_,t)=>({
                  t: t+1,
                  withCache: 1,
                  noCache: t+1,
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="t" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'token position',fill:'var(--t3)',fontSize:9,position:'insideBottom',offset:-2}}/>
                  <YAxis tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'relative FLOPs',fill:'var(--t3)',fontSize:9,angle:-90,position:'insideLeft'}}/>
                  <Tooltip/>
                  <Legend/>
                  <ReferenceLine x={kvCachePos} stroke="#fbbf24" strokeDasharray="5 3" label={{value:'cached up to here',fill:'#fbbf24',fontSize:8}}/>
                  <Line type="monotone" dataKey="noCache" stroke="#f87171" strokeWidth={2} dot={false} name="No cache (O(n²))"/>
                  <Line type="monotone" dataKey="withCache" stroke="#4ade80" strokeWidth={2} dot={false} name="With cache (O(1))"/>
                </LineChart>
              </ResponsiveContainer>
            </Panel>
          </div>
        </div>
      )}

      {/* ── CONTEXT WINDOW ── */}
      {tab==='context-window'&&(
        <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="CONTEXT CONFIG" accent="#a78bfa">
              <Sl label="Context Window" value={ctxSize} min={512} max={200000} step={512} onChange={setCtxSize} color="#a78bfa"/>
              <div style={{marginTop:'8px',fontSize:'9px',color:'var(--t3)',lineHeight:1.7}}>
                Larger context = more info, more memory, slower attention (O(n²)).
              </div>
            </Panel>
            <Panel title="REAL MODEL CONTEXTS" accent="#fbbf24">
              {[
                {name:'GPT-2',ctx:'1,024',color:'#64748b'},
                {name:'GPT-3',ctx:'4,096',color:'#60a5fa'},
                {name:'Claude 2',ctx:'100,000',color:'#fbbf24'},
                {name:'GPT-4',ctx:'128,000',color:'#22d3ee'},
                {name:'Claude 3',ctx:'200,000',color:'#a78bfa'},
                {name:'Gemini 1.5',ctx:'1,000,000',color:'#4ade80'},
              ].map(m=>(
                <div key={m.name} style={{display:'flex',justifyContent:'space-between',marginBottom:'5px',fontSize:'10px',padding:'5px',background:'var(--deep)',borderRadius:'2px',borderLeft:`2px solid ${m.color}`}}>
                  <span style={{color:m.color,fontWeight:700}}>{m.name}</span>
                  <span style={{color:'var(--t3)',fontFamily:'JetBrains Mono'}}>{m.ctx}</span>
                </div>
              ))}
            </Panel>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="ATTENTION COST vs CONTEXT LENGTH (O(n²))" accent="#a78bfa">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={[512,1024,2048,4096,8192,16384,32768,65536,131072,200000].map(n=>({n:n,flops:+(n*n/1e9).toFixed(4),memory:+(n*n*4/1024/1024).toFixed(2)}))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="n" tick={{fontSize:9,fill:'var(--t3)'}} tickFormatter={v=>v>=1000?`${v/1000}K`:v}/>
                  <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                  <Tooltip/>
                  <Legend/>
                  <ReferenceLine x={ctxSize} stroke="#fbbf24" strokeDasharray="5 3" label={{value:`current: ${ctxSize>=1000?ctxSize/1000+'K':ctxSize}`,fill:'#fbbf24',fontSize:8}}/>
                  <Line type="monotone" dataKey="flops" stroke="#a78bfa" strokeWidth={2} dot={false} name="Compute (GFLOPs)"/>
                  <Line type="monotone" dataKey="memory" stroke="#22d3ee" strokeWidth={2} dot={false} name="Attn memory (MB)"/>
                </LineChart>
              </ResponsiveContainer>
            </Panel>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px'}}>
              <Metric label="Context Tokens" value={ctxSize>=1000?`${(ctxSize/1000).toFixed(1)}K`:ctxSize} color="#a78bfa"/>
              <Metric label="~Words" value={`${Math.round(ctxSize*0.75).toLocaleString()}`} color="#22d3ee"/>
              <Metric label="~Pages" value={`${Math.round(ctxSize*0.75/250)}`} color="#4ade80"/>
              <Metric label="Attn FLOPs (M)" value={(ctxSize*ctxSize/1e6).toFixed(1)} color="#fbbf24"/>
            </div>
          </div>
        </div>
      )}

      {/* ── SCALING LAWS ── */}
      {tab==='scaling-laws'&&(
        <div style={{display:'grid',gridTemplateColumns:'180px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="CHINCHILLA SCALING" accent="#fbbf24">
              <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.7,marginBottom:'8px'}}>Optimal: N tokens ≈ 20× parameters. Larger models need proportionally more data.</div>
              <Btn onClick={runScaling} color="#fbbf24" style={{width:'100%'}}>COMPUTE →</Btn>
            </Panel>
            <LogPanel logs={logs} height={200}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {scalingResult
              ? <>
                  <Panel title="CHINCHILLA-OPTIMAL LOSS vs MODEL SIZE" accent="#fbbf24">
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={scalingResult}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="name" tick={{fontSize:10,fill:'var(--t3)'}}/>
                        <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={['auto','auto']}/>
                        <Tooltip/>
                        <Line type="monotone" dataKey="loss" stroke="#fbbf24" strokeWidth={2.5} dot={{r:5,fill:'#fbbf24'}} name="Loss (Chinchilla)"/>
                      </LineChart>
                    </ResponsiveContainer>
                  </Panel>
                  <Panel title="MODEL SIZE vs OPTIMAL TRAINING TOKENS" accent="#22d3ee">
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:'10px',fontFamily:'JetBrains Mono'}}>
                      <thead><tr style={{background:'var(--deep)'}}>
                        {['Model','Params','Optimal Tokens','Loss'].map(h=><th key={h} style={{padding:'6px 10px',textAlign:'left',color:'var(--t3)',fontSize:'9px',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {scalingResult.map((r,i)=>(
                          <tr key={i} style={{borderBottom:'1px solid var(--b0)'}}>
                            <td style={{padding:'6px 10px',color:P[i%P.length],fontWeight:700}}>{r.name}</td>
                            <td style={{padding:'6px 10px',color:'#22d3ee'}}>{r.params}</td>
                            <td style={{padding:'6px 10px',color:'#4ade80'}}>{r.tokens}</td>
                            <td style={{padding:'6px 10px',color:'#fbbf24'}}>{r.loss}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Panel>
                </>
              : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Compute Chinchilla scaling curves</div>
            }
          </div>
        </div>
      )}
    </div>
  );
}
